export interface FantasyPushCredentials {
    accessToken: string;
    accessUsername: string;
}

interface SerializedPushSubscription {
    endpoint: string;
    keys: {
        p256dh: string;
        auth: string;
    };
}

const FANTASY_API_URL =
    "https://fantasy.ftblscore.com/create_league";

function getVapidPublicKey(): string {
    return (
        process.env.REACT_APP_VAPID_PUBLIC_KEY || ""
    ).trim();
}

export function fantasyPushIsConfigured(): boolean {
    return getVapidPublicKey() !== "";
}

function getServiceWorkerUrl(): string {
    return (
        process.env.PUBLIC_URL || ""
    ) + "/service-worker.js";
}

function urlBase64ToArrayBuffer(
    base64String: string
): ArrayBuffer {
    const normalized = base64String
        .replace(/-/g, "+")
        .replace(/_/g, "/");

    const padding = "=".repeat(
        (4 - (normalized.length % 4)) % 4
    );

    const binaryString = window.atob(
        normalized + padding
    );

    /*
        Se crea explícitamente ArrayBuffer para evitar el error
        de TypeScript con Uint8Array<ArrayBufferLike>.
    */
    const buffer = new ArrayBuffer(
        binaryString.length
    );

    const outputArray = new Uint8Array(buffer);

    for (
        let index = 0;
        index < binaryString.length;
        index += 1
    ) {
        outputArray[index] =
            binaryString.charCodeAt(index);
    }

    return buffer;
}

function serializeSubscription(
    subscription: PushSubscription
): SerializedPushSubscription {
    const data = subscription.toJSON();

    const endpoint = subscription.endpoint || "";

    const p256dh = data.keys?.p256dh || "";
    const auth = data.keys?.auth || "";

    if (!endpoint || !p256dh || !auth) {
        throw new Error(
            "El navegador generó una suscripción Push incompleta."
        );
    }

    return {
        endpoint,
        keys: {
            p256dh,
            auth
        }
    };
}

async function getErrorMessage(
    response: Response
): Promise<string> {
    try {
        const result = await response.json();

        if (typeof result?.message === "string") {
            return result.message;
        }
    } catch {
        /*
            La respuesta no era JSON válido.
        */
    }


    return "No fue posible actualizar las notificaciones.";
}

function getRequestHeaders(
    credentials: FantasyPushCredentials
): HeadersInit {
    return {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Access-Token": credentials.accessToken,
        "X-Access-Username": credentials.accessUsername
    };
}

export function fantasyPushIsSupported(): boolean {
    return Boolean(
        window.isSecureContext &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

export async function fantasyPushRegisterServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!fantasyPushIsSupported()) {
        throw new Error(
            "Este navegador no es compatible con notificaciones Push."
        );
    }

    return navigator.serviceWorker.register(
        getServiceWorkerUrl()
    );
}

export async function fantasyPushGetPermission(): Promise<NotificationPermission> {
    if (!fantasyPushIsSupported()) {
        return "denied";
    }

    if (Notification.permission === "granted") {
        return "granted";
    }

    if (Notification.permission === "denied") {
        return "denied";
    }

    return Notification.requestPermission();
}

export async function fantasyPushEnable(
    credentials: FantasyPushCredentials
): Promise<void> {
    const vapidPublicKey = getVapidPublicKey();

    if (!vapidPublicKey) {
        throw new Error(
            "Las notificaciones todavía no están configuradas en el servidor."
        );
    }

    const permission = await fantasyPushGetPermission();

    if (permission !== "granted") {
        throw new Error(
            "No autorizaste las notificaciones en este dispositivo."
        );
    }

    const registration =
        await fantasyPushRegisterServiceWorker();

    let subscription =
        await registration.pushManager.getSubscription();

    if (!subscription) {
        subscription =
            await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey:
                    urlBase64ToArrayBuffer(
                        vapidPublicKey
                    )
            });
    }

    const response = await fetch(
        FANTASY_API_URL +
            "/control_SavePushSubscription.php",
        {
            method: "POST",
            credentials: "include",
            cache: "no-store",
            headers: getRequestHeaders(credentials),
            body: JSON.stringify({
                subscription: serializeSubscription(
                    subscription
                )
            })
        }
    );

    if (!response.ok) {
        throw new Error(
            await getErrorMessage(response)
        );
    }
}

export async function fantasyPushDisable(
    credentials: FantasyPushCredentials
): Promise<void> {
    if (!fantasyPushIsSupported()) {
        return;
    }

    const registration =
        await navigator.serviceWorker.ready;

    const subscription =
        await registration.pushManager.getSubscription();

    if (!subscription) {
        return;
    }

    /*
        Primero se elimina del servidor. Si falla la petición,
        conservamos la suscripción local para poder reintentarlo.
    */
    const response = await fetch(
        FANTASY_API_URL +
            "/control_RemovePushSubscription.php",
        {
            method: "POST",
            credentials: "include",
            cache: "no-store",
            headers: getRequestHeaders(credentials),
            body: JSON.stringify({
                endpoint: subscription.endpoint
            })
        }
    );

    if (!response.ok) {
        throw new Error(
            await getErrorMessage(response)
        );
    }

    await subscription.unsubscribe();
}

export async function fantasyPushIsEnabled(): Promise<boolean> {
    if (
        !fantasyPushIsSupported() ||
        Notification.permission !== "granted"
    ) {
        return false;
    }

    const registration =
        await navigator.serviceWorker.ready;

    const subscription =
        await registration.pushManager.getSubscription();

    return subscription !== null;
}

export async function fantasyPushSync(
    credentials: FantasyPushCredentials
): Promise<void> {
    const vapidPublicKey = getVapidPublicKey();

    if (
        !vapidPublicKey ||
        !fantasyPushIsSupported() ||
        Notification.permission !== "granted"
    ) {
        return;
    }

    const registration =
        await fantasyPushRegisterServiceWorker();

    const subscription =
        await registration.pushManager.getSubscription();

    if (!subscription) {
        return;
    }

    const response = await fetch(
        FANTASY_API_URL +
            "/control_SavePushSubscription.php",
        {
            method: "POST",
            credentials: "include",
            cache: "no-store",
            headers: getRequestHeaders(credentials),
            body: JSON.stringify({
                subscription: serializeSubscription(
                    subscription
                )
            })
        }
    );

    if (!response.ok) {
        throw new Error(
            await getErrorMessage(response)
        );
    }
}
